import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Spin } from 'antd';
import { Link, Redirect } from 'react-router-dom';
import Layout from '../../../components/Layout/Layout';
import { capturePayPalOrder, cancelPayPalOrder } from '../purchaseApi';
import {
  clearPurchaseCheckoutSession,
  isPurchasePreAuth,
  isPurchaseSignedIn,
  PURCHASE_SIGNED_IN_SUCCESS_PATH,
  readPurchaseCheckoutSession,
} from '../purchaseHelpers';
import PurchaseSuccess from './PurchaseSuccess';

function getPayPalCallbackParams(search) {
  const params = new URLSearchParams(search);
  return {
    orderId: params.get('orderId'),
    token: params.get('token'),
  };
}

function CallbackCard({ children }) {
  return (
    <Layout>
      <div className="purchase purchase-callback">
        <div className="purchase-callback-card">{children}</div>
      </div>
    </Layout>
  );
}

export default function PurchasePayPalCallback({ location, mode }) {
  const [status, setStatus] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [captureResult, setCaptureResult] = useState(null);
  const hasRun = useRef(false);
  const session = readPurchaseCheckoutSession();
  const plan = session.plan || null;
  const preAuth = isPurchasePreAuth(location.search);
  const signedIn = isPurchaseSignedIn();

  // The interrupted checkout can be resumed with the saved plan prefilled
  const resumeUrl = plan ? `/purchase?plan=${plan.id}` : '/purchase';

  useEffect(() => {
    // Guard against a duplicate capture if the effect re-fires
    if (hasRun.current) return undefined;
    hasRun.current = true;

    let cancelled = false;

    async function run() {
      const { orderId, token } = getPayPalCallbackParams(location.search);
      if (!orderId) {
        setStatus('error');
        setErrorMessage('Missing order reference. Please try your purchase again.');
        return;
      }

      try {
        if (mode === 'return') {
          const response = await capturePayPalOrder(orderId, token);
          if (cancelled) return;
          setCaptureResult(response.data);
          setStatus('success');
          clearPurchaseCheckoutSession();
          return;
        }

        // Keep the checkout session so the user can resume where they left off
        await cancelPayPalOrder(orderId, token);
        if (cancelled) return;
        setStatus('cancelled');
      } catch (error) {
        if (cancelled) return;
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.title ||
          'We could not confirm your payment. Please try again or contact support.';
        setErrorMessage(message);
        setStatus('error');
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [location.search, mode]);

  if (status === 'loading') {
    return (
      <CallbackCard>
        <Spin size="large" />
        <p className="purchase-callback-title">
          {mode === 'return' ? 'Confirming your payment…' : 'Updating your order…'}
        </p>
        <p className="purchase-callback-note">This only takes a moment — please don&apos;t close this window.</p>
      </CallbackCard>
    );
  }

  // Signed-in purchase return (preAuth) → plan dashboard after capture.
  if (status === 'success' && preAuth && signedIn) {
    return <Redirect to={PURCHASE_SIGNED_IN_SUCCESS_PATH} />;
  }

  if (status === 'success') {
    return (
      <Layout>
        <div className="purchase">
          <PurchaseSuccess
            plan={plan}
            quantity={session.quantity || 1}
            email={session.email}
            captureResult={captureResult}
            preAuth={preAuth}
          />
        </div>
      </Layout>
    );
  }

  if (status === 'cancelled') {
    return (
      <CallbackCard>
        <Alert
          type="warning"
          showIcon
          message="Payment cancelled"
          description="You cancelled checkout on PayPal. No charge was made."
        />
        <div className="purchase-callback-actions">
          <Button type="primary">
            <Link to={resumeUrl}>{plan ? 'Return to checkout' : 'Back to plans'}</Link>
          </Button>
          {plan ? (
            <Button>
              <Link to="/purchase">Back to plans</Link>
            </Button>
          ) : null}
        </div>
      </CallbackCard>
    );
  }

  return (
    <CallbackCard>
      <Alert type="error" showIcon message="Payment could not be completed" description={errorMessage} />
      <div className="purchase-callback-actions">
        <Button type="primary">
          <Link to={resumeUrl}>Try again</Link>
        </Button>
        <Button>
          <Link to="/contact-us">Contact support</Link>
        </Button>
      </div>
    </CallbackCard>
  );
}
